import React from 'react';
import { Box, Typography, Chip, TextField, Stack } from '@mui/material';
import moment from 'moment';
import { AggregatedEvent } from '../../types/calendarTypes';
import { getStatusColor, statusLegend } from '../../utils/calendarUtils';
import { formatTimeDisplay } from '../../utils/gigShiftUtils';
import { formatSkillDescriptions, splitSkillCodes } from '../../utils/skillUtils';

export interface ShiftDetailDraftRow {
    requestSeqId: number;
    date: string;
    hours: string;
}

interface ShiftDetailCardProps {
    event: AggregatedEvent;
    skillLabelByCd: Record<string, string>;
    isEditing: boolean;
    draftNotes: string;
    onDraftNotesChange: (value: string) => void;
    draftRows: ShiftDetailDraftRow[];
    onUpdateDraftRow: (index: number, patch: Partial<{ date: string; hours: string }>) => void;
    draftStartTime: string;
    onDraftStartTimeChange: (value: string) => void;
    draftEndTime: string;
    onDraftEndTimeChange: (value: string) => void;
}

/**
 * Renders the shift details card: Skill, Status, Hours,
 * and Notes. Shared between the click-to-open dialog (month/week views) and
 * the always-visible day-view detail panel, so both surfaces stay in sync.
 */
const ShiftDetailCard: React.FC<ShiftDetailCardProps> = ({
    event,
    skillLabelByCd,
    isEditing,
    draftNotes,
    onDraftNotesChange,
    draftRows,
    onUpdateDraftRow,
    draftStartTime,
    onDraftStartTimeChange,
    draftEndTime,
    onDraftEndTimeChange,
}) => {
    const notes = event.resource.events[0]?.requestNotes;
    const rejectionNotes = event.resource.events.find((e) => e.rejectionNotes)?.rejectionNotes;
    const startTime = event.resource.events[0]?.startTime;
    const endTime = event.resource.events[0]?.endTime;
    const rawSkill = event.resource.skillCd || event.resource.skillDesc || event.resource.skill;
    const skillDisplay = rawSkill ? formatSkillDescriptions(rawSkill, skillLabelByCd) : '';
    const skillCount = rawSkill ? splitSkillCodes(rawSkill).length : 0;
    const skillLabel = skillCount === 1 ? 'Skill' : 'Skills';

    return (
        <Box sx={{ p: 2, backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2 }}>
                <Box>
                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>{skillLabel}</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {skillDisplay || '—'}
                    </Typography>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 2, gridColumn: '1 / -1' }}>
                    {isEditing ? (
                        <Box>
                            <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>Start Time</Typography>
                            <TextField
                                size="small"
                                type="time"
                                value={draftStartTime}
                                onChange={(e) => onDraftStartTimeChange(e.target.value)}
                                sx={{ mt: 0.5, width: '100%' }}
                            />
                        </Box>
                    ) : (
                        <Box>
                            <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>Start Time</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {formatTimeDisplay(startTime) || '—'}
                            </Typography>
                        </Box>
                    )}
                    {isEditing ? (
                        <Box>
                            <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>End Time</Typography>
                            <TextField
                                size="small"
                                type="time"
                                value={draftEndTime}
                                onChange={(e) => onDraftEndTimeChange(e.target.value)}
                                sx={{ mt: 0.5, width: '100%' }}
                            />
                        </Box>
                    ) : ( 
                        <Box>
                            <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>End Time</Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {formatTimeDisplay(endTime) || '—'}
                            </Typography>
                        </Box> 
                    )}
                </Box>
                <Box>
                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>Status</Typography>
                    <Chip
                        label={statusLegend.find(s => s.status === event.resource.status)?.label || event.resource.status}
                        size="small"
                        sx={{
                            backgroundColor: getStatusColor(event.resource.status),
                            color: 'white',
                            fontWeight: 600,
                            fontSize: '0.75rem',
                            mt: 0.5
                        }}
                    />
                </Box>
                <Box>
                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>
                        {isEditing ? 'Date' : ''}
                    </Typography>
                    {isEditing ? (
                        <Stack spacing={1} sx={{ mt: 0.5 }}>
                            {draftRows.map((row, idx) => (
                                <Stack key={row.requestSeqId} direction="row" spacing={1} alignItems="center">
                                    <TextField
                                        size="small"
                                        type="date"
                                        value={row.date}
                                        onChange={(e) => onUpdateDraftRow(idx, { date: e.target.value })}
                                        inputProps={{ min: moment().format('YYYY-MM-DD') }}
                                        sx={{ maxWidth: 160 }}
                                    />
                                </Stack>
                            ))}
                        </Stack>
                    ) : (
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            
                        </Typography>
                    )}
                </Box>
                <Box>
                    <Typography variant="body2" sx={{ color: '#666', fontSize: '0.875rem' }}>Notes</Typography>
                    {isEditing ? (
                        <TextField
                            size="small"
                            value={draftNotes}
                            onChange={(e) => onDraftNotesChange(e.target.value)}
                            multiline
                            minRows={2}
                            maxRows={4}
                            sx={{ mt: 0.5, width: '100%' }}
                        />
                    ) : (
                        <Typography
                            variant="body1"
                            sx={{
                                fontWeight: 500,
                                maxHeight: 96,
                                overflowY: 'auto',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                pr: 1,
                            }}
                        >
                                                    {notes || '—'}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                        {event.resource.status === 'REJECTED' && rejectionNotes && (
                                            <Box sx={{
                                                mt: 2,
                                                p: 1.5,
                                                backgroundColor: 'rgba(212, 0, 41, 0.06)',
                                                borderLeft: '3px solid #D40029',
                                                borderRadius: '0 6px 6px 0',
                                            }}>
                                                <Typography variant="body2" sx={{ color: '#D40029', fontWeight: 600, fontSize: '0.75rem', mb: 0.5 }}>
                                                    Rejection Notes
                                                </Typography>
                                                <Typography
                                                    variant="body1"
                                                    sx={{
                                                        fontWeight: 500,
                                                        whiteSpace: 'pre-wrap',
                                                        wordBreak: 'break-word',
                                                    }}
                                                >
                                                    {rejectionNotes}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                );
                            };

export default ShiftDetailCard;
